import { Button } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DataTable } from '@/components/data-table/data-table';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { CUSTOM_TRADING_FEES_QUERY_KEY } from '@/hooks/queries/fees/use-fees';
import { getErrorMessage } from '@/lib/api/error';
import { type CustomTradingFee, deleteCustomTradingFees } from '@/services/api/fees';
import { CustomTradingFeeDialog } from './custom-trading-fee-dialog';
import { FeesErrorBanner } from './fees-error-banner';

const EMPTY_MESSAGE = 'No custom trading fees data available';

// Render the stored value verbatim (string or number) with a % suffix
const PercentCell = ({ value }: { value: string | number }) => <span className="tabular-nums">{value}%</span>;
PercentCell.displayName = 'PercentCell';

interface DialogState {
  /** null → add mode */
  fee: CustomTradingFee | null;
}

export interface CustomTradingFeesPanelProps {
  fees: CustomTradingFee[] | undefined;
  isLoading: boolean;
  errorMessage: string | null;
  onRetry: () => void;
}

/** Custom trading fees tab: add button, per-account fee table, edit/delete. */
export const CustomTradingFeesPanel = ({ fees, isLoading, errorMessage, onRetry }: CustomTradingFeesPanelProps) => {
  const queryClient = useQueryClient();
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  const handleDelete = useCallback(
    async (fee: CustomTradingFee) => {
      const justification = await confirmAction({
        title: 'Confirm Delete custom trading fee',
        description: `Are you sure you want to delete the custom trading fee for ${fee.accountHolderId}? This action cannot be undone.`,
        actionLabel: 'Delete custom trading fee',
        destructive: true,
        onConfirm: async (comment) => {
          try {
            await deleteCustomTradingFees(fee.accountHolderId, comment);
          } catch (error) {
            // Failure copy — rethrown so the audit dialog stays open showing it
            throw new Error(`Error deleting fees: ${getErrorMessage(error)}`);
          }
        },
      });
      if (justification === null) return;

      toast.success(`Successfully deleted custom trading fee for ${fee.accountHolderId}`);
      await queryClient.invalidateQueries({ queryKey: CUSTOM_TRADING_FEES_QUERY_KEY });
    },
    [queryClient],
  );

  const handleEdit = useCallback((fee: CustomTradingFee) => setDialogState({ fee }), []);

  const columns = useMemo(
    (): ColumnDef<CustomTradingFee, unknown>[] => [
      {
        accessorKey: 'accountHolderId',
        header: 'Account Holder ID',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.accountHolderId}</span>,
      },
      { accessorKey: 'makerFeeSpot', header: 'Maker Fee (SPOT)', cell: ({ row }) => <PercentCell value={row.original.makerFeeSpot} /> },
      { accessorKey: 'takerFeeSpot', header: 'Taker Fee (SPOT)', cell: ({ row }) => <PercentCell value={row.original.takerFeeSpot} /> },
      { accessorKey: 'makerFeeFutures', header: 'Maker Fee (FUTURES)', cell: ({ row }) => <PercentCell value={row.original.makerFeeFutures} /> },
      { accessorKey: 'takerFeeFutures', header: 'Taker Fee (FUTURES)', cell: ({ row }) => <PercentCell value={row.original.takerFeeFutures} /> },
      {
        id: 'actions',
        enableSorting: false,
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button aria-label={`Edit custom trading fee for ${row.original.accountHolderId}`} onClick={() => handleEdit(row.original)} size="icon" type="button" variant="ghost">
              <Pencil className="size-4" />
            </Button>
            <Button
              aria-label={`Delete custom trading fee for ${row.original.accountHolderId}`}
              onClick={() => void handleDelete(row.original)}
              size="icon"
              type="button"
              variant="ghost"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ),
      },
    ],
    [handleEdit, handleDelete],
  );

  return (
    <div className="flex max-w-[1200px] flex-col gap-3">
      <div>
        <Button className="gap-2" onClick={() => setDialogState({ fee: null })} type="button">
          <Plus className="size-4" />
          Add Custom Trading Fee
        </Button>
      </div>

      {errorMessage ? (
        <FeesErrorBanner message={errorMessage} onRetry={onRetry} />
      ) : (
        <DataTable columns={columns} data={fees} emptyMessage={EMPTY_MESSAGE} getRowId={(row) => row.accountHolderId} isLoading={isLoading} />
      )}

      {dialogState ? <CustomTradingFeeDialog fee={dialogState.fee} onClose={() => setDialogState(null)} /> : null}
    </div>
  );
};
CustomTradingFeesPanel.displayName = 'CustomTradingFeesPanel';
